import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  IconButton,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid, Checkbox, Chip, InputAdornment, TextField, Radio,
  Slider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  MergeType as MergeTypeIcon,
  Search as SearchIcon,
  Language as LanguageIcon,
  CloseOutlined,
} from "@mui/icons-material";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from "../../../constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import { useSystemTheme } from "context/SystemThemeContext";
import { getAccessToken } from "utils/tokenManager";

const SizeStyle = Quill.import("attributors/style/size");
SizeStyle.whitelist = ["11px", "13px", "15px", "18px", "24px"];
Quill.register(SizeStyle, true);
const SITE_URL = process.env.REACT_APP_SITE_URL || "";
const toImgDataUrl = (raw) => {
  if (!raw || raw === "null" || raw === "undefined") return null;
  if (typeof raw === "string" && raw.startsWith("data:"))     return raw;
  if (typeof raw === "string" && raw.startsWith("/uploads/")) return `${SITE_URL}${raw}`;
  try { return `data:image/png;base64,${raw}`; } catch { return null; }
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const getPlainTextLength = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").length;
};

const MAX_CHARS = 300;

const QUILL_MODULES = {
  toolbar: [[{ size: ["11px", false, "15px", "18px", "24px"] }, "bold", "italic", "underline", "link"]],
  clipboard: { matchVisual: false },
  keyboard: {
    bindings: {
      enter: {
        key: 13,
        shiftKey: false,
        handler() {
          const quill = this.quill;
          const range = quill.getSelection();
          if (range) quill.insertText(range.index, "\n", "user");
          return false;
        },
      },
    },
  },
};
const QUILL_FORMATS = ["bold", "italic", "underline", "link", "size"];
const EMPTY_SMTP = { smtp_host: "", smtp_port: "", smtp_username: "", smtp_password: "" };

const validateSmtp = (values, hasExistingPassword) => {
  const errs = {};
  if (!values.smtp_host.trim())     errs.smtp_host     = "Host is required";
  if (!values.smtp_port.trim())     errs.smtp_port     = "Port is required";
  else if (!/^\d+$/.test(values.smtp_port) || +values.smtp_port < 1 || +values.smtp_port > 65535)
    errs.smtp_port = "Must be 1–65535";
  if (!values.smtp_username.trim()) errs.smtp_username = "Username is required";
  if (!hasExistingPassword && !values.smtp_password.trim())
    errs.smtp_password = "Password is required for initial setup";
  return errs;
};

const SmtpInput = ({
  label, value, onChange, placeholder,
  type = "text", maxLength, error, style = {}, children, isDarkMode,
}) => {
  const [focused, setFocused] = useState(false);
  const borderColor = error ? "#d32f2f" : focused ? "#219297" : (isDarkMode ? "#4a5568" : "#ddd");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px", ...style }}>
      <label style={{
        fontSize: "11px", fontWeight: 600,
        color: error ? "#d32f2f" : focused ? "#219297" : (isDarkMode ? "#a0aec0" : "#aaa"),
        letterSpacing: "0.04em", textTransform: "uppercase", transition: "color 0.15s",
      }}>
        {label.replace(/\*/g, '')}
        {label.includes('*') && (
          <span style={{ color: "#d32f2f", marginLeft: "2px" }}>*</span>
        )}
      </label>

      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          maxLength={maxLength}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%",
            padding: "9px 12px",
            paddingRight: children ? "38px" : "12px",
            fontSize: "13.5px",
            color: isDarkMode ? "#e2e8f0" : "#344767",
            backgroundColor: isDarkMode ? "#2d2d3f" : "#fff",
            border: `1.5px solid ${borderColor}`,
            borderRadius: "8px",
            outline: "none",
            boxSizing: "border-box",
            transition: "border-color 0.15s, background-color 0.3s",
            fontFamily: "inherit",
          }}
          onMouseEnter={(e) => { if (!focused && !error) e.target.style.borderColor = "#219297"; }}
          onMouseLeave={(e) => { if (!focused && !error) e.target.style.borderColor = error ? "#d32f2f" : (isDarkMode ? "#4a5568" : "#ddd"); }}
        />
        {children && (
          <div style={{ position: "absolute", right: "10px", display: "flex", alignItems: "center" }}>
            {children}
          </div>
        )}
      </div>

      {error && (
        <span style={{ fontSize: "11px", color: "#d32f2f", marginTop: "2px" }}>{error}</span>
      )}
    </div>
  );
};

const SmtpSettings = ({ values, onChange, errors, hasExistingPassword, isDarkMode }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <div style={{ display: "flex", gap: "12px" }}>
        <SmtpInput
          label="SMTP Host *"
          value={values.smtp_host}
          onChange={(e) => onChange("smtp_host", e.target.value)}
          placeholder="smtp.example.com"
          error={errors.smtp_host}
          style={{ flex: 1 }}
          isDarkMode={isDarkMode}
        />
        <SmtpInput
          label="SMTP Port *"
          value={values.smtp_port}
          onChange={(e) => onChange("smtp_port", e.target.value.replace(/\D/g, ""))}
          placeholder="Enter port"
          maxLength={5}
          error={errors.smtp_port}
          style={{ width: "120px", flexShrink: 0 }}
          isDarkMode={isDarkMode}
        />
      </div>

      <SmtpInput
        label="SMTP Username *"
        value={values.smtp_username}
        placeholder="user@example.com"
        onChange={(e) => onChange("smtp_username", e.target.value)}
        error={errors.smtp_username}
        isDarkMode={isDarkMode}
      />
      <SmtpInput
        label="SMTP Password *"
        type={showPassword ? "text" : "password"}
        value={values.smtp_password}
        placeholder={hasExistingPassword ? "Edit existing password" : "Enter password"}
        onChange={(e) => onChange("smtp_password", e.target.value)}
        error={errors.smtp_password}
        isDarkMode={isDarkMode}
      >
        <button
          type="button" onClick={() => setShowPassword((p) => !p)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "2px", display: "flex", alignItems: "center",
            color: isDarkMode ? "#a0aec0" : "#bbb", transition: "color 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#219297"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? "#a0aec0" : "#bbb"; }}
        >
          {showPassword ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          )}
        </button>
      </SmtpInput>

      {hasExistingPassword && (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: "6px",
          backgroundColor: isDarkMode ? "#0a1020" : "#f5fbfb", 
          border: `1px solid ${isDarkMode ? "#4a5568" : "#c8e6e7"}`,
          borderRadius: "6px", padding: "8px 10px",
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#219297" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: "1px", flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span style={{ fontSize: "11.5px", color: isDarkMode ? "#a0aec0" : "#219297", lineHeight: 1.5 }}>
            A password is already stored securely. Enter a new one only if you want to update it.
          </span>
        </div>
      )}
    </div>
  );
};

const Section = ({ label, children, isDarkMode }) => (
  <Box mb={3.5}>
    <Typography variant="caption" sx={{
      fontWeight: 600, color: isDarkMode ? "#a0aec0" : "#888", 
      letterSpacing: "0.08em", textTransform: "uppercase", display: "block", mb: 1.25,
    }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const LogoUploadBox = ({
  existingUrl, previewUrl, newFile, onSelect,
  inputId = "logo-upload",
  label = "Click to upload",
  isDarkMode,
}) => {
  const displayUrl = previewUrl || existingUrl;
  return (
    <Box
      onClick={() => document.getElementById(inputId).click()}
      sx={{
        border: "1.5px dashed", 
        borderColor: displayUrl ? "#219297" : (isDarkMode ? "#4a5568" : "#ddd"),
        borderRadius: "8px", p: 2, display: "flex", alignItems: "center", gap: 2,
        cursor: "pointer", 
        backgroundColor: displayUrl 
          ? (isDarkMode ? "rgba(33,146,151,0.1)" : "#f5fbfb")
          : (isDarkMode ? "#0a1020" : "#fafafa"),
        transition: "all 0.2s",
        "&:hover": { borderColor: "#219297", backgroundColor: isDarkMode ? "#1a2542" : "#f5fbfb" },
      }}
    >
      <input
        id={inputId} type="file" accept="image/*"
        onChange={onSelect} style={{ display: "none" }}
      />
      {displayUrl ? (
        <>
          <img
            src={displayUrl} alt="Logo"
            onError={(e) => { e.target.style.display = "none"; }}
            style={{
              width: 48, height: 48, objectFit: "contain",
              borderRadius: 4, border: `1px solid ${isDarkMode ? "#4a5568" : "#e0e0e0"}`, 
              background: isDarkMode ? "#060C18" : "#fff",
            }}
          />
          <Box>
            <Typography variant="body2" fontWeight="500" color="#219297" fontSize="13px">
              {newFile ? newFile.name : "Current logo"}
            </Typography>
            <Typography variant="caption" color={isDarkMode ? "#a0aec0" : "#aaa"}>Click to replace</Typography>
          </Box>
        </>
      ) : (
        <>
          <CloudUploadIcon sx={{ color: isDarkMode ? "#a0aec0" : "#bbb", fontSize: 26 }} />
          <Box>
            <Typography variant="body2" fontWeight="500" color={isDarkMode ? "#e2e8f0" : "#666"} fontSize="13px">
              {label}
            </Typography>
            <Typography variant="caption" color={isDarkMode ? "#a0aec0" : "#aaa"}>PNG, JPG, JPEG · max 2 MB</Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

const DisclaimerEditor = ({ value, onChange, isDarkMode }) => {
  const [charCount, setCharCount] = useState(() => getPlainTextLength(value));
  const quillRef = useRef(null);
  const isOverLimit = charCount > MAX_CHARS;
  const isNearLimit = !isOverLimit && charCount > MAX_CHARS * 0.85;
  const borderColor = isOverLimit ? "#d32f2f" : (isDarkMode ? "#4a5568" : "#ddd");

  const handleChange = (html, _delta, _source, editor) => {
    const len = editor.getText().replace(/\n$/, "").length;
    if (len > MAX_CHARS) {
      const quill = quillRef.current?.getEditor();
      if (quill) quill.deleteText(MAX_CHARS, quill.getText().length);
      return;
    }
    setCharCount(len);
    onChange(html, len);
  };

  return (
    <Box>
      <Box sx={{
        "& .ql-toolbar.ql-snow": {
          border: `1.5px solid ${borderColor}`, borderBottom: "none",
          borderRadius: "8px 8px 0 0", 
          backgroundColor: isDarkMode ? "#0a1020" : "#fafafa", 
          padding: "4px 8px",
          "& button": { height: "22px", width: "22px", padding: "2px" },
          "& button svg": { width: "14px", height: "14px" },
          "& .ql-stroke": { stroke: isDarkMode ? "#a0aec0" : "#888" },
          "& .ql-fill": { fill: isDarkMode ? "#a0aec0" : "#888" },
          "& button:hover .ql-stroke, & button.ql-active .ql-stroke": { stroke: "#219297" },
          "& button:hover .ql-fill,   & button.ql-active .ql-fill": { fill: "#219297" },
          "& .ql-picker-label": { color: isDarkMode ? "#a0aec0" : "#888", fontSize: "12px" },
          "& .ql-size .ql-picker-label": { minWidth: "52px" },
          "& .ql-size .ql-picker-label::before": { content: "'Normal'" },
          "& .ql-size .ql-picker-label[data-value='11px']::before": { content: "'Small'" },
          "& .ql-size .ql-picker-label[data-value='15px']::before": { content: "'Medium'" },
          "& .ql-size .ql-picker-label[data-value='18px']::before": { content: "'Large'" },
          "& .ql-size .ql-picker-label[data-value='24px']::before": { content: "'Huge'" },
          "& .ql-size .ql-picker-item::before": { content: "'Normal'" },
          "& .ql-size .ql-picker-item[data-value='11px']::before": { content: "'Small'", fontSize: "11px" },
          "& .ql-size .ql-picker-item[data-value='15px']::before": { content: "'Medium'", fontSize: "15px" },
          "& .ql-size .ql-picker-item[data-value='18px']::before": { content: "'Large'", fontSize: "18px" },
          "& .ql-size .ql-picker-item[data-value='24px']::before": { content: "'Huge'", fontSize: "24px" },
          "& .ql-picker-options": { 
            borderRadius: "8px", 
            boxShadow: "0 4px 16px rgba(33,146,151,0.12)", 
            border: `1px solid ${isDarkMode ? "#4a5568" : "#e8eef0"}`,
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
          },
          "& .ql-picker-item:hover": { color: "#219297 !important" },
        },
        "& .ql-container.ql-snow": {
          border: `1.5px solid ${borderColor}`, borderRadius: "0 0 8px 8px",
          fontSize: "13.5px", fontFamily: "inherit",
          backgroundColor: isDarkMode ? "#060C18" : "#fff",
        },
        "& .ql-editor": {
          minHeight: "90px", padding: "10px 12px", 
          color: isDarkMode ? "#e2e8f0" : "#344767", 
          lineHeight: 1.65,
          "& a": { color: "#219297" },
          "& p": { margin: "0 0 2px 0" },
        },
        "& .ql-editor.ql-blank::before": { 
          color: isDarkMode ? "#a0aec0" : "#bbb", 
          fontSize: "13.5px", 
          fontStyle: "normal" 
        },
        "&:focus-within .ql-toolbar.ql-snow": { borderColor: isOverLimit ? "#d32f2f" : "#219297" },
        "&:focus-within .ql-container.ql-snow": { borderColor: isOverLimit ? "#d32f2f" : "#219297" },
      }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Powered by Ansera.AI"
        />
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={0.5}>
        <Typography variant="caption" fontWeight="600" sx={{
          color: isOverLimit ? "#d32f2f" : isNearLimit ? "#f57c00" : (isDarkMode ? "#a0aec0" : "#ccc"), 
          transition: "color 0.2s",
        }}>
          {charCount}/{MAX_CHARS}
        </Typography>
      </Box>
    </Box>
  );
};

export default function WhitelabelBrandingPage() {
  const { mode } = useSystemTheme();
  const isDarkMode = mode === "dark";
  
  const { userId } = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();

  const [loading,      setLoading]      = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error,        setError]        = useState("");
  const [errorSnackbar, setErrorSnackbar] = useState({
  open: false,
  message: ""
});
  const [successOpen,  setSuccessOpen]  = useState(false);
  const [userData,     setUserData]     = useState(null);
  const [confirmOpen,  setConfirmOpen]  = useState(false);

  const [logoFile,        setLogoFile]        = useState(null);
  const [logoPreviewUrl,  setLogoPreviewUrl]  = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState(null);
  const [savedLogoUrl,    setSavedLogoUrl]    = useState(null);

  const [bubbleLogoFile,        setBubbleLogoFile]        = useState(null);
  const [bubbleLogoPreviewUrl,  setBubbleLogoPreviewUrl]  = useState(null);
  const [existingBubbleLogoUrl, setExistingBubbleLogoUrl] = useState(null);
  const [savedBubbleLogoUrl,    setSavedBubbleLogoUrl]    = useState(null);

  const [htmlContent,   setHtmlContent]   = useState("");
  const [htmlCharCount, setHtmlCharCount] = useState(0);
  const [savedHtml,     setSavedHtml]     = useState("");


  const [smtpValues,          setSmtpValues]          = useState(EMPTY_SMTP);
  const [savedSmtp,           setSavedSmtp]           = useState(EMPTY_SMTP);
  const [smtpErrors,          setSmtpErrors]          = useState({});
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [allowMergeDomains,setAllowMergeDomains]= useState(false);
  const accessToken= getAccessToken()
  const smtpDirty = JSON.stringify(smtpValues) !== JSON.stringify(savedSmtp);
  const isDirty   = htmlContent !== savedHtml
    || logoFile !== null
    || bubbleLogoFile !== null
    || smtpDirty;

  const smtpTouched =
    smtpValues.smtp_host || smtpValues.smtp_port ||
    smtpValues.smtp_username || smtpValues.smtp_password;

  const liveSmtpErrors = smtpTouched ? validateSmtp(smtpValues, hasExistingPassword) : {};
  const smtpBlocksSave = Object.keys(liveSmtpErrors).length > 0;
  const targetUserId   = userId || user?.id;

  useEffect(() => { if (targetUserId) fetchUserData(); }, [targetUserId]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl)       URL.revokeObjectURL(logoPreviewUrl);
      if (bubbleLogoPreviewUrl) URL.revokeObjectURL(bubbleLogoPreviewUrl);
    };
  }, [logoPreviewUrl, bubbleLogoPreviewUrl]);

  const fetchUserData = async () => {
    setFetchLoading(true);
    setError("");
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/reseller-user/${targetUserId}`, {
        headers: { "x-api-key": process.env.REACT_APP_API_KEY,"Authorization": `Bearer ${accessToken}` },
      });

      if (res.data.resultCode === "0" && res.data.result) {
        const u = res.data.result;
        setUserData(u);
        setAllowMergeDomains(u.allow_merge_domains)

        if (u.whitelabel_settings) {
            const logoPath = u.whitelabel_settings.company_logo;

           const logoUrl = logoPath
             ? `${logoPath}`
             : null;
                   setExistingLogoUrl(logoUrl);
                   setSavedLogoUrl(logoUrl);

                   // Bubble logo
                   const BubblelogoPath = u.whitelabel_settings.search_bar_bubble_logo;
                  const bubbleUrl = BubblelogoPath
             ? `${BubblelogoPath}`
             : null;
                   setExistingBubbleLogoUrl(bubbleUrl);
                   setSavedBubbleLogoUrl(bubbleUrl);
          const disclaimer = u.whitelabel_settings.custom_disclaimer_text || "";
          setHtmlContent(disclaimer);
          setHtmlCharCount(getPlainTextLength(disclaimer));
          setSavedHtml(disclaimer);
        }
        try {
          const smtpRes = await axios.get(
            `${FAST_API_BASE_URL}/api/smtp-settings/${targetUserId}`,
            { headers: { "x-api-key": process.env.REACT_APP_API_KEY,"Authorization": `Bearer ${accessToken}` } }
          );
          if (smtpRes.data.resultCode === "0" && smtpRes.data.result) {
            const s = smtpRes.data.result;
            const populated = {
              smtp_host:     s.smtp_host     || "",
              smtp_port:     s.smtp_port     ? String(s.smtp_port) : "",
              smtp_username: s.smtp_username || "",
              smtp_password: "",
            };
            setSmtpValues(populated);
            setSavedSmtp(populated);
            setHasExistingPassword(!!s.has_smtp_password);
          }
        } catch (_) {
     
        }

      } else {
        setError(res.data.messages?.[0] || "Failed to load user data");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load user data");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please select a valid image file"); return; }
    if (file.size > 2 * 1024 * 1024)    { setError("File size must be less than 2 MB"); return; }
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setExistingLogoUrl(null);
    setError("");
  };
const handleCloseErrorSnackbar = () => {
  setErrorSnackbar({ open: false, message: "" });
};
  const handleBubbleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Please select a valid image file"); return; }
    if (file.size > 5 * 1024 * 1024)    { setError("File size must be less than 5 MB"); return; }
    if (bubbleLogoPreviewUrl) URL.revokeObjectURL(bubbleLogoPreviewUrl);
    setBubbleLogoFile(file);
    setBubbleLogoPreviewUrl(URL.createObjectURL(file));
    setExistingBubbleLogoUrl(null);
    setError("");
  };

  const handleSmtpChange = (field, value) => {
    setSmtpValues((prev) => ({ ...prev, [field]: value }));
    setSmtpErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCancel = () => {
    setHtmlContent(savedHtml);
    setHtmlCharCount(getPlainTextLength(savedHtml));
    if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setExistingLogoUrl(savedLogoUrl);
    if (bubbleLogoPreviewUrl) URL.revokeObjectURL(bubbleLogoPreviewUrl);
    setBubbleLogoFile(null);
    setBubbleLogoPreviewUrl(null);
    setExistingBubbleLogoUrl(savedBubbleLogoUrl);
    setSmtpValues(savedSmtp);
    setSmtpErrors({});
    setError("");
  };

  const handleSaveClick = () => {
    if (smtpTouched) {
      const errs = validateSmtp(smtpValues, hasExistingPassword);
      setSmtpErrors(errs);
      if (Object.keys(errs).length > 0) return;
    }
    setConfirmOpen(true);
  };

  const handleSave = async () => {
    setConfirmOpen(false);
    if (htmlCharCount > MAX_CHARS) { 
    setError(`Content exceeds ${MAX_CHARS} characters.`); 
    return; 
  }
    setLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("user_id", targetUserId);
      formData.append("is_whitelabel_user", "true");
      if (htmlContent)    formData.append("custom_disclaimer_text", htmlContent);
      if (logoFile)       formData.append("company_logo",           logoFile);
      if (bubbleLogoFile) formData.append("search_bar_bubble_logo", bubbleLogoFile);

      const wlRes = await axios.patch(
        `${FAST_API_BASE_URL}/api/user-whitelabel-settings`,
        formData,
        { headers: { "x-api-key": process.env.REACT_APP_API_KEY,"Authorization": `Bearer ${accessToken}` } }
      );
      if (wlRes.data.resultType !== "Success") {
        setError(wlRes.data.message || "Failed to save settings");
        return;
      }

      const savedCompanyLogoPath     = wlRes.data.result?.company_logo            || existingLogoUrl;
      const savedBubbleLogoPath      = wlRes.data.result?.search_bar_bubble_logo  || existingBubbleLogoUrl;

    if (smtpDirty) { 
      const smtpPayload = {
        smtp_host: smtpValues.smtp_host,
        smtp_port: smtpValues.smtp_port ? Number(smtpValues.smtp_port) : null,
        smtp_username: smtpValues.smtp_username,
        smtp_password: smtpValues.smtp_password || undefined, 
      };
      
      try {
        const smtpRes = await axios.patch(
          `${FAST_API_BASE_URL}/api/smtp-settings/${targetUserId}`,
          smtpPayload,
          { headers: { "x-api-key": process.env.REACT_APP_API_KEY,"Authorization": `Bearer ${accessToken}` } }
        );
        
        if (smtpRes.data.resultType !== "Success") {
          setErrorSnackbar({
            open: true,
            message: "Failed to save settings. Please check your credentials"
          });

          return;
        }
      } catch (smtpErr) {
         setErrorSnackbar({
          open: true,
          message: "Failed to save settings.Please check your credentials"
        });

        return;
      }
    }
      if (userData?.domain_details?.length > 0) {
        const domainPayload = {
          is_whitelabelled: true,
          ...(htmlContent && { custom_disclaimer_text: htmlContent }),
          ...(savedCompanyLogoPath && { company_logo: savedCompanyLogoPath }),
          ...(savedBubbleLogoPath && { ansera_search_bubble_icon_url: savedBubbleLogoPath }),
          ...(savedBubbleLogoPath && { search_bar_bubble_logo: savedBubbleLogoPath }),
        };

      const domainUpdatePromises = userData.domain_details.map((d) =>
        axios
          .patch(`${LAMBDA_BASE_URL}/settings`, domainPayload, {
            headers: {
              "domain-token": d.domain_token,
              "x-api-key": process.env.REACT_APP_API_KEY,
              "Content-Type": "application/json",
            },
          })
          .catch((err) => {
            console.error(`Domain ${d.domain_token}:`, err);
            return null;
          })
      );
      
      await Promise.allSettled(domainUpdatePromises);
    }

      setSavedHtml(htmlContent);

      if (logoFile && logoPreviewUrl) {
        setExistingLogoUrl(logoPreviewUrl);
        setSavedLogoUrl(logoPreviewUrl);
        setLogoPreviewUrl(null);
      }
      setLogoFile(null);

      if (bubbleLogoFile && bubbleLogoPreviewUrl) {
        setExistingBubbleLogoUrl(bubbleLogoPreviewUrl);
        setSavedBubbleLogoUrl(bubbleLogoPreviewUrl);
        setBubbleLogoPreviewUrl(null);
      }
      setBubbleLogoFile(null);

      const nextSmtp = { ...smtpValues, smtp_password: "" };
      setSmtpValues(nextSmtp);
      setSavedSmtp(nextSmtp);
      if (smtpValues.smtp_password) setHasExistingPassword(true);

      setSmtpErrors({});
      setSuccessOpen(true);
      fetchUserData();
    } catch (err) {
      console.error("Save error:", err);
      setError(err.response?.data?.message || "Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldLabels = {
    smtp_host: "SMTP Host",
    smtp_port: "SMTP Port",
    smtp_username: "Username",
    smtp_password: "Password",
  };

  const missingFields = Object.keys(liveSmtpErrors).map((key) => fieldLabels[key]);

  if (fetchLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox pt={6} pb={3}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress size={26} sx={{ color: "#219297" }} />
          </Box>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox pt={4} pb={5}>
        <Box maxWidth="860px" mx="auto" px={2}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <IconButton size="small" onClick={() => navigate(-1)}
              sx={{ 
                color: "#219297", 
                p: 0.5, 
                "&:hover": { backgroundColor: isDarkMode ? "#1a2542" : "#f0f9fa" } 
              }}>
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography variant="h6" fontWeight="600" color="#219297" lineHeight={1}>
              Whitelabel Settings
            </Typography>
          </Box>

          {userData && (
            <Typography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "#aaa", display: "block", ml: 0.5, mb: 3 }}>
              {userData.name} · {userData.email}
              {userData.domain_details?.length > 0 && ` · ${userData.domain_details.length} domain(s)`}
            </Typography>
          )}

          <Snackbar
            open={errorSnackbar.open}
            autoHideDuration={5000}
            onClose={handleCloseErrorSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            message={errorSnackbar.message}
            ContentProps={{
              sx: {
                backgroundColor: "#d32f2f",
                fontSize: "16px",
                borderRadius: "8px",
                minWidth: "unset"
              },
            }}
            action={
              <IconButton size="small" color="inherit" onClick={handleCloseErrorSnackbar}>
                <CloseOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            }
          />

          <Box sx={{ 
            border: `1px solid ${isDarkMode ? "#1a2542" : "#ebebeb"}`, 
            borderRadius: "10px", 
            backgroundColor: isDarkMode ? "#060C18" : "#fff", 
            overflow: "hidden",
            transition: "background-color 0.3s ease, border-color 0.3s ease",
          }}>
            {loading && (
              <LinearProgress sx={{ height: 2, "& .MuiLinearProgress-bar": { backgroundColor: "#219297" } }} />
            )}

            <Box p={3}>
              <Section label="Custom Disclaimer Text" isDarkMode={isDarkMode}>
                <Typography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "#bbb", display: "block", mb: 1.5, lineHeight: 1.5 }}>
                  Supports bold, italic, underline, font size &amp; links · {MAX_CHARS} char limit · Enter for new line
                </Typography>
                <DisclaimerEditor
                  value={htmlContent}
                  onChange={(html, len) => { setHtmlContent(html); setHtmlCharCount(len); }}
                  isDarkMode={isDarkMode}
                />
              </Section>

              <Box sx={{ borderTop: `1px solid ${isDarkMode ? "#1a2542" : "#f2f2f2"}`, my: 3 }} />
              
              <Section label="Company Logo" isDarkMode={isDarkMode}>
                <Typography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "#bbb", display: "block", mb: 1.5, lineHeight: 1.5 }}>
                  Displayed in the search bar and chatbot · PNG, JPG · max 2 MB
                </Typography>
                <LogoUploadBox
                  existingUrl={existingLogoUrl}
                  previewUrl={logoPreviewUrl}
                  newFile={logoFile}
                  onSelect={handleLogoSelect}
                  inputId="company-logo-upload"
                  label="Click to upload company logo"
                  isDarkMode={isDarkMode}
                />
              </Section>

              <Box sx={{ borderTop: `1px solid ${isDarkMode ? "#1a2542" : "#f2f2f2"}`, my: 3 }} />

              <Section label="Search Bar Bubble Logo" isDarkMode={isDarkMode}>
                <Typography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "#bbb", display: "block", mb: 1.5, lineHeight: 1.5 }}>
                  Displayed when search bar is closed · PNG, JPG · max 2 MB
                </Typography>
                <LogoUploadBox
                  existingUrl={existingBubbleLogoUrl}
                  previewUrl={bubbleLogoPreviewUrl}
                  newFile={bubbleLogoFile}
                  onSelect={handleBubbleLogoSelect}
                  inputId="bubble-logo-upload"
                  label="Click to upload bubble logo"
                  isDarkMode={isDarkMode}
                />
              </Section>

              <Box sx={{ borderTop: `1px solid ${isDarkMode ? "#1a2542" : "#f2f2f2"}`, my: 3 }} />

              <Section label="SMTP Configuration" isDarkMode={isDarkMode}>
                <Typography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "#bbb", display: "block", mb: 1.5, lineHeight: 1.5 }}>
                  Used to send emails from your own mail server · All 4 fields required
                </Typography>
                <SmtpSettings
                  values={smtpValues}
                  onChange={handleSmtpChange}
                  errors={smtpErrors}
                  hasExistingPassword={hasExistingPassword}
                  isDarkMode={isDarkMode}
                />
              </Section>
            </Box>

            {isDirty && (
              <Box sx={{
                px: 3, py: 2, 
                borderTop: `1px solid ${isDarkMode ? "#1a2542" : "#f2f2f2"}`, 
                backgroundColor: isDarkMode ? "#0a1020" : "#fafafa",
                display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5,
              }}>
                <Typography
                  variant="caption"
                  mr="auto"
                  sx={{
                    color: smtpBlocksSave ? "#d32f2f" : (isDarkMode ? "#a0aec0" : "#bbb"),
                    fontWeight: smtpBlocksSave ? 500 : 400,
                  }}
                >
                  {smtpBlocksSave && smtpTouched
                    ? `Fill required fields: ${missingFields.join(", ")} to Save.`
                    : htmlCharCount > MAX_CHARS
                      ? `Disclaimer exceeds ${MAX_CHARS} characters.`
                      : "You have unsaved changes."
                  }
                </Typography>
                <Button
                  onClick={handleCancel} disabled={loading} size="small"
                  sx={{
                    color: isDarkMode ? "#a0aec0" : "#aaa", 
                    fontSize: "13px", textTransform: "none", minWidth: "unset",
                    "&:hover": { backgroundColor: isDarkMode ? "#2d2d3f" : "#f5f5f5", color: isDarkMode ? "#e2e8f0" : "#666" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveClick} variant="contained" size="small"
                  disabled={loading || htmlCharCount > MAX_CHARS || smtpBlocksSave}
                  startIcon={<SaveIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    backgroundColor: "#219297", "&:hover": { backgroundColor: "#1a7579" },
                    "&:disabled": { backgroundColor: isDarkMode ? "#2d2d3f" : "#e8e8e8", color: isDarkMode ? "#a0aec0" : "#bbb" },
                    color: "#fff", fontSize: "13px", textTransform: "none", px: 2.5, boxShadow: "none",
                  }}
                >
                  {loading ? "Saving…" : "Save"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </SoftBox>

      <Dialog 
        open={confirmOpen} 
        onClose={() => setConfirmOpen(false)} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#060C18" : "#fff",
            borderRadius: "16px",
          }
        }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, color: isDarkMode ? "#e2e8f0" : undefined }}>
          Confirm Save
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Typography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "#555" }}>
            Are you sure you want to save the changes?
          </Typography>
          {smtpTouched && (
            <Typography variant="caption" sx={{ color: isDarkMode ? "#a0aec0" : "#aaa", display: "block", mt: 1 }}>
              SMTP credentials will be verified via a live connection before saving.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}
            sx={{ color: isDarkMode ? "#a0aec0" : "#888", textTransform: "none", fontSize: "13px" }}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" sx={{
            backgroundColor: "#219297", "&:hover": { backgroundColor: "#1a7579" },
            color: "#fff", textTransform: "none", fontWeight: 600, fontSize: "13px", boxShadow: "none",
          }}>
            Yes, Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen} autoHideDuration={3000} onClose={() => setSuccessOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message="Settings saved"
        ContentProps={{
          sx: { backgroundColor: "#219297", fontSize: "13px", borderRadius: "8px", minWidth: "unset" },
        }}
      />
    </DashboardLayout>
  );
}